import type { Connection, TokenBalance } from '@solana/web3.js';

import { hashDiagnosticEntity } from '../../../../tx-engine/diagnostics/lifecycle.js';
import type { TradeMutationDeps } from '../types.js';
import { showClaimSuccessToast } from './toasts.js';

type ClaimKind = 'claim_prize' | 'claim_backer_fees';

type ClaimSuccessDeps = Pick<
  TradeMutationDeps,
  | 't'
  | 'showToast'
  | 'dismissToast'
  | 'getExplorerUrl'
  | 'openExternalUrl'
  | 'formatBalanceD6'
  | 'getConnection'
  | 'logger'
  | 'onClaimConfirmed'
>;

interface ConfirmedClaim {
  kind: ClaimKind;
  traceId: string;
  signature: string;
  marketAddress: string;
  walletAddress: string;
}

/** The RPC can briefly return `null` for a transaction it just confirmed. */
const READ_ATTEMPTS = 4;
const READ_RETRY_DELAY_MS = 500;
/** Quote mints (USDC) carry 6 decimals; burned claim/share mints only ever debit. */
const QUOTE_DECIMALS = 6;

/**
 * Finishes a confirmed claim: reads the exact quote amount the chain credited
 * to the claimer, shows "$X added to your wallet", and hands the host its
 * celebration hook. Never throws — the claim has already landed, so a failed
 * payout read degrades to the generic confirmation.
 */
export async function announceConfirmedClaim(
  deps: ClaimSuccessDeps,
  claim: ConfirmedClaim,
): Promise<void> {
  const payoutD6 = await readPayoutOrNull(deps, claim);
  showClaimSuccessToast(deps, claim.traceId, claim.signature, payoutD6);
  deps.onClaimConfirmed?.({ kind: claim.kind, marketAddress: claim.marketAddress, payoutD6 });
}

async function readPayoutOrNull(
  deps: ClaimSuccessDeps,
  claim: ConfirmedClaim,
): Promise<string | null> {
  try {
    const payoutD6 = await readClaimPayoutD6(
      deps.getConnection(),
      claim.signature,
      claim.walletAddress,
    );
    if (payoutD6 === null) logPayoutReadFailed(deps, claim, 'no_single_quote_credit');
    return payoutD6;
  } catch (err: unknown) {
    logPayoutReadFailed(deps, claim, err instanceof Error ? err.message : String(err));
    return null;
  }
}

function logPayoutReadFailed(deps: ClaimSuccessDeps, claim: ConfirmedClaim, reason: string): void {
  deps.logger.warn('claim.payout_read_failed', {
    kind: claim.kind,
    traceId: claim.traceId,
    reason,
    marketHash: hashDiagnosticEntity(claim.marketAddress),
  });
}

export async function readClaimPayoutD6(
  connection: Pick<Connection, 'getTransaction'>,
  signature: string,
  walletAddress: string,
  sleep: (ms: number) => Promise<void> = defaultSleep,
): Promise<string | null> {
  for (let attempt = 1; attempt <= READ_ATTEMPTS; attempt += 1) {
    const tx = await connection.getTransaction(signature, {
      commitment: 'confirmed',
      maxSupportedTransactionVersion: 0,
    });
    if (tx?.meta != null) {
      return creditedQuoteD6(
        tx.meta.preTokenBalances ?? [],
        tx.meta.postTokenBalances ?? [],
        walletAddress,
      );
    }
    if (attempt < READ_ATTEMPTS) await sleep(READ_RETRY_DELAY_MS);
  }
  return null;
}

/**
 * Sum of the wallet's positive 6-decimal token balance changes, provided they
 * all land on one mint — the claim payout. Anything else (no credit, or credits
 * on several mints) is ambiguous and returns `null` rather than a guess.
 */
export function creditedQuoteD6(
  preBalances: readonly TokenBalance[],
  postBalances: readonly TokenBalance[],
  walletAddress: string,
): string | null {
  let creditedMint: string | null = null;
  let creditedD6 = BigInt(0);
  for (const post of postBalances) {
    if (post.owner !== walletAddress || post.uiTokenAmount.decimals !== QUOTE_DECIMALS) continue;
    const pre = preBalances.find((balance) => balance.accountIndex === post.accountIndex);
    const delta = BigInt(post.uiTokenAmount.amount) - BigInt(pre?.uiTokenAmount.amount ?? '0');
    if (delta <= BigInt(0)) continue;
    if (creditedMint !== null && creditedMint !== post.mint) return null;
    creditedMint = post.mint;
    creditedD6 += delta;
  }
  return creditedMint === null ? null : creditedD6.toString();
}

function defaultSleep(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}
